import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trophy, Plus, Edit, Trash2, Calendar, MapPin, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminTournamentsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'daily',
    category: 'דרג א',
    date: '',
    time: '',
    location: 'מרכז הטניס רעננה',
    max_players: 16,
    description: '',
    prizes: []
  });

  useEffect(() => {
    if (!user || !user.is_admin) {
      toast.error('אין לך הרשאות אדמין');
      navigate('/');
      return;
    }
    fetchTournaments();
  }, [user, navigate]);

  const fetchTournaments = async () => {
    try {
      const response = await axios.get(`${API}/api/tournaments`);
      setTournaments(response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת תחרויות');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTournament = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/tournaments`, formData);
      toast.success('תחרות נוצרה בהצלחה! 🎾');
      setShowCreateForm(false);
      setFormData({
        name: '',
        type: 'daily',
        category: 'דרג א',
        date: '',
        time: '',
        location: 'מרכז הטניס רעננה',
        max_players: 16,
        description: '',
        prizes: []
      });
      fetchTournaments();
    } catch (error) {
      toast.error('שגיאה ביצירת תחרות');
    }
  };

  const handleDeleteTournament = async (tournamentId, tournamentName) => {
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק את "${tournamentName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/tournaments/${tournamentId}`);
      toast.success('תחרות נמחקה בהצלחה');
      fetchTournaments();
    } catch (error) {
      toast.error('שגיאה במחיקת תחרות');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">טוען...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              🏆 ניהול תחרויות
            </h1>
            <p className="text-gray-600">יצירה, עריכה ומחיקה של תחרויות</p>
          </div>
          <Button 
            className="btn-primary flex items-center gap-2"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            <Plus className="w-5 h-5" />
            תחרות חדשה
          </Button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <Card className="mb-8 glass-effect">
            <CardHeader>
              <CardTitle>יצירת תחרות חדשה</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateTournament} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>שם התחרות *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="תחרות יומית דרג א"
                      required
                    />
                  </div>

                  <div>
                    <Label>סוג תחרות *</Label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value, max_players: e.target.value === 'daily' ? 16 : 32})}
                    >
                      <option value="daily">יומית (16 שחקנים)</option>
                      <option value="knockout">נוקאאוט (32 שחקנים)</option>
                    </select>
                  </div>

                  <div>
                    <Label>קטגוריה *</Label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                    >
                      <option value="דרג א">דרג א</option>
                      <option value="דרג ב">דרג ב</option>
                      <option value="דרג ג">דרג ג</option>
                      <option value="נשים">נשים</option>
                      <option value="זוגות open">זוגות open</option>
                      <option value="יחידים open">יחידים open</option>
                    </select>
                  </div>

                  <div>
                    <Label>מיקום *</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="מרכז הטניס רעננה"
                      required
                    />
                  </div>

                  <div>
                    <Label>תאריך *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <Label>שעה *</Label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>תיאור</Label>
                  <textarea
                    className="w-full px-3 py-2 border rounded-md"
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="תיאור התחרות..."
                  />
                </div>

                <div className="flex gap-4">
                  <Button type="submit" className="btn-primary">
                    צור תחרות
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                  >
                    ביטול
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Tournaments List */}
        <div className="grid grid-cols-1 gap-6">
          {tournaments.length === 0 ? (
            <Card className="glass-effect">
              <CardContent className="py-12 text-center text-gray-500">
                אין תחרויות להצגה
              </CardContent>
            </Card>
          ) : (
            tournaments.map((tournament) => (
              <Card key={tournament.id} className="glass-effect hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Trophy className="w-6 h-6 text-blue-600" />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {tournament.name}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {tournament.date} | {tournament.time}
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {tournament.location}
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            {tournament.registered_players?.length || 0} / {tournament.max_players} שחקנים
                          </div>
                          <div>
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                              tournament.status === 'open' ? 'bg-green-100 text-green-800' :
                              tournament.status === 'full' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {tournament.status === 'open' ? 'פתוח' :
                               tournament.status === 'full' ? 'מלא' : 'הושלם'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/results/${tournament.id}`)}
                        className="flex items-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        תוצאות
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteTournament(tournament.id, tournament.name)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTournamentsPage;
